import {
    Button,
    Card,
    CardBody,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Tag,
    TagLabel,
    Text,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { RiStackLine } from 'react-icons/ri';

import { FilterType } from '../_openapi/portfolio-optimizer';
import { SIdQuote, SIdQuoteSource } from '../_openapi/pricing';
import { useForceUpdateSidPricers, usePriceList } from '../api/pricing';
import { AssetDrawer, useAssetDrawerControls } from '../Asset/AssetDrawer/AssetDrawer';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { parseFilterStringToObject } from '../widgets/filters/filterUtils';
import { NumberFilterMode, NumberFilterValue } from '../widgets/filters/NumberFilter';
import { PageHeader } from '../widgets/PageHeader';
import { baseCol, dateTimeCol, moneyCol, numberCol, textCol } from '../widgets/Table/columnHelpers/colDef';
import { ControlToggle } from '../widgets/Table/ControlToggle';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';

const columnHelper = createColumnHelper<SIdQuote>();
export const PricingList = () => {
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();
    const { sorting, setSorting, resetTable, columnFilters, setColumnFilters } = usePersistentTableState(dataColumns);

    const { data, isLoading } = usePriceList();
    const [missingPriceFilter, setMissingPriceFilter] = useState(false);

    const pricingData = useMemo(() => {
        if (!data) {
            return [];
        }

        let filtered = [...data];
        if (missingPriceFilter) {
            filtered = filtered.filter(({ bid, ask }) => bid === '0' || ask === '0');
        }

        if (columnFilters.length) {
            filtered = columnFilters.reduce((result, columnFilter) => {
                const column = dataColumns.find((col) => col.id === columnFilter.id);
                const filterType = column?.meta?.filterType;

                return result.filter((quote) => {
                    const value = quote[columnFilter.id as keyof typeof quote] as string;

                    if (filterType === FilterType.LIST) {
                        const filterValue = columnFilter.value as string[];
                        return filterValue.some((v: string) => value === v);
                    }

                    if (filterType === FilterType.NUMBER) {
                        const filterValue = parseFilterStringToObject(
                            columnFilter.value as string,
                        ) as NumberFilterValue;

                        if (filterValue.eq) {
                            return value === filterValue.eq;
                        }

                        const condition = ([op, v]: [string, string]) => {
                            switch (op) {
                                case 'gt':
                                    return Number(value) > Number(v);
                                case 'gte':
                                    return Number(value) >= Number(v);
                                case 'lt':
                                    return Number(value) < Number(v);
                                case 'lte':
                                    return Number(value) <= Number(v);
                            }
                        };

                        const { mode, ...operators } = filterValue;
                        if (mode === NumberFilterMode.OR) {
                            return Object.entries(operators).some(condition);
                        } else {
                            return Object.entries(operators).every(condition);
                        }
                    }

                    // FilterType.STRING
                    const filterValue = columnFilter.value as string[];
                    return filterValue.some((v: string) => value.includes(v));
                });
            }, filtered);
        }

        if (sorting?.length) {
            filtered = sortBy(filtered, sorting[0].id);
            if (sorting[0].desc) {
                filtered = filtered.reverse();
            }
        }

        return filtered;
    }, [columnFilters, data, missingPriceFilter, sorting]);

    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: 'detailsBtn',
                cell: ({ row }) =>
                    row.subRows.length ? null : (
                        <IconButton
                            aria-label=""
                            onClick={() => viewAsset(row.original.sId ?? '')}
                            size="xs"
                            icon={<Icon as={RiStackLine} />}
                            h={5}
                            w={5}
                            minW={5}
                            variant="outline"
                        />
                    ),
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...dataColumns,
        ];
    }, [viewAsset]);

    const table = useReactTable({
        data: pricingData,
        columns,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: DEFAULT_PAGE_SIZE,
            },
        },
        state: {
            sorting,
            columnFilters,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualFiltering: true,
        manualSorting: true,
    });

    return (
        <>
            <PageHeader title="Pricing">
                <PricerUpdateButton />
            </PageHeader>
            <Card>
                <CardBody>
                    <TableControls table={table} onReset={resetTable}>
                        <ControlToggle
                            label="Securities with missing Price"
                            onChange={setMissingPriceFilter}
                            isChecked={missingPriceFilter}
                        />
                    </TableControls>
                    <Table table={table} isLoading={isLoading} allowXOverflow />
                    <Pagination table={table} />
                </CardBody>
            </Card>
            <AssetDrawer {...assetDrawerProps} />
        </>
    );
};

const PricerUpdateButton = () => {
    const { mutate: forceUpdateSidPricers, isPending: forceUpdateSidPricersLoading } = useForceUpdateSidPricers();
    const { isOpen, onOpen, onClose } = useDisclosure();

    return (
        <>
            <Tooltip
                label={
                    <Stack>
                        <Text>
                            Outside of trading hours the cache is not guaranteed to have prices for some securities,
                            they are available on a best effort basis.
                        </Text>
                        <Text>Pressing the button will force-refresh the Pricing service cache</Text>
                    </Stack>
                }
            >
                <Button
                    variant="outline"
                    colorScheme="teal"
                    size="sm"
                    onClick={onOpen}
                    isLoading={forceUpdateSidPricersLoading}
                >
                    Update Pricers
                </Button>
            </Tooltip>
            <Modal isOpen={isOpen} onClose={onClose} size="2xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Refresh pricing</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>Are you sure you want to trigger a refresh?</Text>
                    </ModalBody>

                    <ModalFooter justifyContent="space-between">
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={() => {
                                forceUpdateSidPricers();
                                onClose();
                            }}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

const dataColumns = [
    columnHelper.accessor('source', {
        ...baseCol({ id: 'source' }),
        cell: ({ getValue }) => {
            const value = getValue() as SIdQuoteSource;
            return (
                <Tag size="sm" colorScheme={value === 'MANUAL' ? 'red' : 'gray'}>
                    <TagLabel>{value}</TagLabel>
                </Tag>
            );
        },
        meta: {
            filterType: FilterType.LIST,
            filterOptionsFn: () => Object.values(SIdQuoteSource).sort(),
        },
    }),
    columnHelper.accessor('sId', textCol({ id: 'sId' })),
    columnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    columnHelper.accessor('bid', moneyCol({ id: 'bid', format: { fractionDigits: 4 } })),
    columnHelper.accessor('bidSize', numberCol({ id: 'bidSize' })),
    columnHelper.accessor('ask', moneyCol({ id: 'ask', format: { fractionDigits: 4 } })),
    columnHelper.accessor('askSize', numberCol({ id: 'askSize' })),
    columnHelper.accessor('timestamp', {
        ...dateTimeCol({ id: 'timestamp' }),
        enableColumnFilter: false,
        enableSorting: false,
    }),
];
